import { useMemo } from "react"
import { useOrderStatistics } from "../../../hooks/useOrderStatistics"
import ApiError from "../../../components/ApiError"
import LinkButton from "../../../components/LinkButton"
import { useNavigate } from "react-router-dom"
import routePaths from "../../../constants/path"
import { DateTime } from "luxon"
import Loader from "../../../components/Loader"

export const PatientOrdersStatistics = ({ filters, sorting }) => {
  const navigate = useNavigate()

  const { data, isLoading, error, isError } = useOrderStatistics({
    sortBy: sorting.map((s) => `${s.id}:${s.desc ? "DESC" : "ASC"}`).join(","),
    to: filters.to,
    from: filters.from,
    patientIssuer: filters.patientIssuer,
  })

  const orderStats = useMemo(() => data?.data || [], [data])

  if (isError) {
    return (
      <div className="mt-4">
        <ApiError axiosError={error} />
      </div>
    )
  }

  return (
    <div className="rounded-lg overflow-hidden shadow-sm">
      <div className="bg-gray-100">
        <div className="px-3 py-6 font-bold text-xl text-start w-full bg-primary text-white rounded-t-lg">
          Orders Statistics
        </div>
      </div>

      {isLoading ? (
        <div className="py-10 flex justify-center items-center gap-3">
          <Loader className="border-primary" />
          <p>Loading Orders...</p>
        </div>
      ) : (
        <div className="w-full overflow-x-auto bg-white rounded-b-lg">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className="bg-gray-100 text-left p-3 border border-gray-300"></th>
                <th
                  className="text-center p-3 border border-gray-300"
                  colSpan="2"
                >
                  CLIENT MIRTH
                </th>
                <th
                  className="text-center p-3 border border-gray-300"
                  colSpan="2"
                >
                  MAIN MIRTH
                </th>
                <th
                  className="text-center p-3 border border-gray-300"
                  colSpan="2"
                >
                  PACS
                </th>
              </tr>
              <tr>
                <th className="bg-gray-100 text-left p-3 border border-gray-300">
                  Patient Issuer
                </th>
                <th className="bg-gray-100 text-center p-3 border border-gray-300">
                  ACCEPTED
                </th>
                <th className="bg-gray-100 text-center p-3 border border-gray-300">
                  REJECTED
                </th>
                <th className="bg-gray-100 text-center p-3 border border-gray-300">
                  ACCEPTED
                </th>
                <th className="bg-gray-100 text-center p-3 border border-gray-300">
                  REJECTED
                </th>
                <th className="bg-gray-100 text-center p-3 border border-gray-300">
                  ACCEPTED
                </th>
                <th className="bg-gray-100 text-center p-3 border border-gray-300">
                  REJECTED
                </th>
              </tr>
            </thead>
            <tbody>
              {orderStats.length ? (
                orderStats.map((stat) => (
                  <tr key={stat.PatientIssuer} className="hover:bg-gray-100">
                    <td className="p-3 border border-gray-300">
                      <LinkButton
                        onClick={() => {
                          const fromISO = DateTime.fromJSDate(
                            new Date(filters.from)
                          ).toISODate() // "2025-08-18"
                          const toISO = DateTime.fromJSDate(
                            new Date(filters.to)
                          ).toISODate()
                          const patientIssuer = encodeURIComponent(
                            stat.PatientIssuer
                          )

                          navigate(
                            `${routePaths.orders}?patientIssuer=${patientIssuer}&from=${fromISO}&to=${toISO}`
                          )
                        }}
                      >
                        {stat.PatientIssuer}
                      </LinkButton>
                    </td>
                    <td className="p-3 border border-gray-300 text-center">
                      <span className="text-green-600">
                        <span className="material-icons text-sm align-middle mr-1">
                          check
                        </span>
                        {stat.TotalAcceptedOrdersByClientMirth}
                      </span>
                    </td>
                    <td className="p-3 border border-gray-300 text-center">
                      <span className="text-red-600">
                        <span className="material-icons text-sm align-middle mr-1">
                          close
                        </span>
                        {stat.TotalRejectedOrdersByClientMirth}
                      </span>
                    </td>
                    <td className="p-3 border border-gray-300 text-center">
                      <span className="text-green-600">
                        <span className="material-icons text-sm align-middle mr-1">
                          check
                        </span>
                        {stat.TotalAcceptedOrdersByMainMirth}
                      </span>
                    </td>
                    <td className="p-3 border border-gray-300 text-center">
                      <span className="text-red-600">
                        <span className="material-icons text-sm align-middle mr-1">
                          close
                        </span>
                        {stat.TotalRejectedOrdersByMainMirth}
                      </span>
                    </td>
                    <td className="p-3 border border-gray-300 text-center">
                      <span className="text-green-600">
                        <span className="material-icons text-sm align-middle mr-1">
                          check
                        </span>
                        {stat.TotalAcceptedOrdersByPACS}
                      </span>
                    </td>
                    <td className="p-3 border border-gray-300 text-center">
                      <span className="text-red-600">
                        <span className="material-icons text-sm align-middle mr-1">
                          close
                        </span>
                        {stat.TotalRejectedOrdersByPACS}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td className="py-10 text-center bg-white" colSpan="7">
                    <p>No orders found</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default PatientOrdersStatistics
